import type { LookKind } from "../ui/view-model";

/**
 * What can be picked in the tech demo (docs/SPEC.md section 11; docs/handoff/008-items-looks.md). Locks are fixed for now:
 * achievements and the shop arrive in later slices. The shell lists must match `SHELLS` and `PATS` in ui/Shell.tsx.
 */
export type Lock = "shop" | "achievement" | null;
export interface CatalogueEntry {
  name: string;
  lock: Lock;
}

const free = (name: string): CatalogueEntry => ({ name, lock: null });
const shop = (name: string): CatalogueEntry => ({ name, lock: "shop" });
const earned = (name: string): CatalogueEntry => ({ name, lock: "achievement" });

export const CATALOGUE: Record<LookKind, readonly CatalogueEntry[]> = {
  toy: [free("Ball"), free("Hoop"), shop("Rubber duck")],
  companion: [free("Little fish"), free("Crab"), earned("Turtle")],
  decoration: [free("Coral"), free("Treasure chest"), earned("Anchor")],
  background: [free("Lagoon"), free("Sunset reef"), shop("Deep night")],
  shellColour: [free("Pastel"), free("Jelly"), free("Teal"), free("Orange"), free("Plum"), earned("Rose")],
  shellPattern: [free("Plain"), free("Dots"), shop("Stripes")],
};

const SLOTS: readonly LookKind[] = ["toy", "companion", "decoration"];

/** Slots can be emptied ("None", index -1); backgrounds and shells always have one picked. */
export function canBeNone(kind: LookKind): boolean {
  return SLOTS.includes(kind);
}

/**
 * What stops a pick right now. An "achievement" item is open once it was granted; if it was not granted and the 12 week window is
 * over, it can no longer be earned and is for the shop (docs/SPEC.md section 5).
 */
export function effectiveLock(entry: CatalogueEntry, granted: boolean, dripOpen: boolean, owned = false): Lock {
  if (owned) return null;
  if (entry.lock !== "achievement") return entry.lock;
  if (granted) return null;
  return dripOpen ? "achievement" : "shop";
}

export function lockMessage(lock: Exclude<Lock, null>): string {
  return lock === "shop" ? "Find it in the shop." : "Earn it with an achievement.";
}
